/*
 * Hook de donnees de la page d'administration : chargement, etat groupe par
 * section et enregistrement. La cle administrateur vit UNIQUEMENT en memoire
 * (jamais en sessionStorage ni localStorage).
 */

import { useState } from "react";
import { toast } from "sonner";
import { adminGetSettings, adminUpdateSettings } from "@/lib/api";

export const ACTION_CATEGORIES = [
  { key: "sync", label: "Compréhension et synchronisation" },
  { key: "energie", label: "Énergie, sommeil et récupération" },
  { key: "entrainement", label: "Cohérence de l'activité physique" },
  { key: "nutrition", label: "Nutrition et habitudes" },
  { key: "constance", label: "Charge mentale et constance" },
  { key: "prete", label: "Profil globalement équilibré (au moins trois actions)" },
  { key: "prudence", label: "Prudence santé : étapes d'orientation non sportives (au moins trois actions)" },
];

export const DIMENSION_LABELS = [
  { key: "sync", label: "Synchronisation" },
  { key: "energie", label: "Énergie" },
  { key: "entrainement", label: "Activité physique" },
  { key: "nutrition", label: "Nutrition" },
  { key: "constance", label: "Constance" },
];

function relanceStateFromSettings(data) {
  const relance = data.relance_settings || {};
  const emails = Array.isArray(relance.emails) ? relance.emails : [];
  const cancellation = relance.cancellation || {};
  return {
    enabled: Boolean(relance.enabled),
    delays: (relance.delays_days || [7, 14, 21]).map((d) => String(d)),
    emails: [0, 1, 2].map((i) => ({
      subject: emails[i]?.subject || "",
      body: emails[i]?.body || "",
    })),
    cancellation: {
      enabled: Boolean(cancellation.enabled),
      subject: cancellation.subject || "",
      body: cancellation.body || "",
    },
  };
}

/* Transformateurs par categorie : parametres serveur -> etat du formulaire. */

const brandFromSettings = (data) => ({
  programName: data.program_name || "",
  logoUrl: data.logo_url || "",
});

const conversionFromSettings = (data) => ({
  bookingUrl: data.booking_url || "",
  priceReference: data.program_price_reference || "",
  capacity: data.cohort_capacity ?? 15,
});

const scoringFromSettings = (data) => {
  const config = data.scoring_config || {};
  return {
    baremeVersion: config.bareme_version || "2.0.0",
    readyThreshold: config.profile_ready_threshold ?? 70,
    weights: { ...(config.dimension_weights || {}) },
    invertedQuestions: (config.inverted_questions || []).join(", "),
    restitutionLevels: JSON.stringify(config.restitution_levels || [], null, 2),
  };
};

const consentsFromSettings = (data) => {
  const consents = data.consent_texts || {};
  return {
    version: consents.version || "",
    sensible: consents.sensible || "",
    service: consents.service || "",
    marketing: consents.marketing || "",
    phone: consents.phone || "",
  };
};

const actionsTextFromSettings = (data) => {
  const lib = data.actions_library || {};
  const actionsText = {};
  ACTION_CATEGORIES.forEach(({ key }) => {
    actionsText[key] = (lib[key] || []).join("\n");
  });
  return actionsText;
};

const weeklyReportFromSettings = (data) => {
  const report = data.weekly_report || {};
  return { enabled: Boolean(report.enabled), recipient: report.recipient || "" };
};

const objectiveFromSettings = (data) => {
  const objective = data.objective_settings || {};
  return {
    target: String(objective.target ?? 15),
    windowDays: String(objective.window_days ?? 90),
    startDate: objective.start_date || "",
  };
};

const fitFromSettings = (data) => {
  const fit = data.fit_config || {};
  return {
    programName: fit.program_name || "CYCLIQ EXCELLENCE",
    programDuration: fit.program_duration || "90 jours",
    priceFull: fit.price_full || "",
    priceInstallments: fit.price_installments || "",
    priceInstallmentsTotal: fit.price_installments_total || "",
    thresholdHigh: String(fit.threshold_high ?? 75),
    thresholdQualified: String(fit.threshold_qualified ?? 55),
  };
};

function stateFromSettings(data) {
  return {
    brand: brandFromSettings(data),
    conversion: conversionFromSettings(data),
    scoring: scoringFromSettings(data),
    consents: consentsFromSettings(data),
    privacySections: (data.privacy_sections || []).map((s) => ({ ...s })),
    actionsText: actionsTextFromSettings(data),
    relance: relanceStateFromSettings(data),
    weeklyReport: weeklyReportFromSettings(data),
    objective: objectiveFromSettings(data),
    fit: fitFromSettings(data),
  };
}

/* Fabriques de mise a jour partielle : evitent la repetition des setters. */
const patchObjectOf = (setter) => (patch) => setter((prev) => ({ ...prev, ...patch }));
const patchFieldOf = (setter) => (field, value) => setter((prev) => ({ ...prev, [field]: value }));

export function useAdminSettings() {
  const [adminKey, setAdminKey] = useState("");
  const [authError, setAuthError] = useState("");
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const [brand, setBrandState] = useState({ programName: "", logoUrl: "" });
  const [conversion, setConversionState] = useState({ bookingUrl: "", priceReference: "", capacity: 15 });
  const [scoring, setScoringState] = useState({ baremeVersion: "", readyThreshold: 70, weights: {}, invertedQuestions: "", restitutionLevels: "" });
  const [consents, setConsentsState] = useState({ version: "", sensible: "", service: "", marketing: "", phone: "" });
  const [privacySections, setPrivacySections] = useState([]);
  const [actionsText, setActionsText] = useState({});
  const [relance, setRelanceState] = useState({
    enabled: false,
    delays: ["7", "14", "21"],
    emails: [{ subject: "", body: "" }, { subject: "", body: "" }, { subject: "", body: "" }],
    cancellation: { enabled: false, subject: "", body: "" },
  });
  const [weeklyReport, setWeeklyReportState] = useState({ enabled: false, recipient: "" });
  const [objective, setObjectiveState] = useState({ target: "15", windowDays: "90", startDate: "" });
  const [fit, setFitState] = useState({
    programName: "CYCLIQ EXCELLENCE", programDuration: "90 jours",
    priceFull: "", priceInstallments: "", priceInstallmentsTotal: "",
    thresholdHigh: "75", thresholdQualified: "55",
  });

  const patchBrand = patchObjectOf(setBrandState);
  const patchConversion = patchObjectOf(setConversionState);
  const patchScoring = patchObjectOf(setScoringState);
  const patchConsents = patchObjectOf(setConsentsState);
  const patchRelance = patchObjectOf(setRelanceState);
  const patchWeeklyReport = patchFieldOf(setWeeklyReportState);
  const patchObjective = patchFieldOf(setObjectiveState);
  const patchFit = patchFieldOf(setFitState);

  const patchRelanceCancellation = (field, value) => {
    setRelanceState((prev) => ({
      ...prev,
      cancellation: { ...prev.cancellation, [field]: value },
    }));
  };
  const patchRelanceEmail = (index, field, value) => {
    setRelanceState((prev) => ({
      ...prev,
      emails: prev.emails.map((e, i) => (i === index ? { ...e, [field]: value } : e)),
    }));
  };
  const patchRelanceDelay = (index, value) => {
    setRelanceState((prev) => ({
      ...prev,
      delays: prev.delays.map((d, i) => (i === index ? value : d)),
    }));
  };

  const patchAction = (key, value) => setActionsText((prev) => ({ ...prev, [key]: value }));
  const updateSection = (index, field, value) => {
    setPrivacySections((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const applyState = (state) => {
    setBrandState(state.brand);
    setConversionState(state.conversion);
    setScoringState(state.scoring);
    setConsentsState(state.consents);
    setPrivacySections(state.privacySections);
    setActionsText(state.actionsText);
    setRelanceState(state.relance);
    setWeeklyReportState(state.weeklyReport);
    setObjectiveState(state.objective);
    setFitState(state.fit);
  };

  const loadSettings = async (key) => {
    try {
      const data = await adminGetSettings(key);
      setSettings(data);
      applyState(stateFromSettings(data));
      setAuthError("");
      setAdminKey(key);
    } catch (e) {
      setAuthError("Clé administrateur invalide.");
      setAdminKey("");
    }
  };

  const buildUpdates = () => {
    const actionsLibrary = {};
    ACTION_CATEGORIES.forEach(({ key }) => {
      actionsLibrary[key] = (actionsText[key] || "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
    });
    const parsedLevels = JSON.parse(scoring.restitutionLevels || "[]");
    return {
      booking_url: conversion.bookingUrl,
      program_price_reference: conversion.priceReference,
      cohort_capacity: Number(conversion.capacity),
      program_name: brand.programName,
      logo_url: brand.logoUrl,
      actions_library: actionsLibrary,
      scoring_config: {
        bareme_version: scoring.baremeVersion.trim(),
        profile_ready_threshold: Number(scoring.readyThreshold),
        dimension_weights: Object.fromEntries(
          DIMENSION_LABELS.map(({ key }) => [key, Number(scoring.weights[key] ?? 0)])
        ),
        inverted_questions: scoring.invertedQuestions
          .split(",")
          .map((q) => q.trim().toLowerCase())
          .filter(Boolean),
        restitution_levels: parsedLevels,
      },
      consent_texts: { ...consents },
      privacy_sections: privacySections,
      relance_settings: {
        enabled: relance.enabled,
        delays_days: relance.delays.map((d) => Number(d)),
        emails: relance.emails.map((e) => ({ subject: e.subject, body: e.body })),
        cancellation: { ...relance.cancellation },
      },
      weekly_report: { ...weeklyReport },
      objective_settings: {
        target: Number(objective.target),
        window_days: Number(objective.windowDays),
        start_date: objective.startDate,
      },
      fit_config: {
        program_name: fit.programName,
        program_duration: fit.programDuration,
        price_full: fit.priceFull,
        price_installments: fit.priceInstallments,
        price_installments_total: fit.priceInstallmentsTotal,
        threshold_high: Number(fit.thresholdHigh),
        threshold_qualified: Number(fit.thresholdQualified),
      },
    };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let updates;
      try {
        updates = buildUpdates();
      } catch (e) {
        toast.error("Erreur", { description: "Les niveaux de restitution doivent être un JSON valide." });
        return;
      }
      const updated = await adminUpdateSettings(adminKey, updates);
      setSettings(updated);
      toast.success("Paramètres enregistrés", { description: "Les modifications sont actives immédiatement." });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Enregistrement impossible. Vérifie les champs.";
      toast.error("Erreur", { description: typeof detail === "string" ? detail : "Vérifie les champs saisis." });
    } finally {
      setSaving(false);
    }
  };

  return {
    settings, saving, authError, loadSettings, handleSave, adminKey,
    brand, patchBrand,
    conversion, patchConversion,
    scoring, patchScoring,
    consents, patchConsents,
    privacySections, updateSection,
    actionsText, patchAction,
    relance, patchRelance, patchRelanceEmail, patchRelanceDelay, patchRelanceCancellation,
    weeklyReport, patchWeeklyReport,
    objective, patchObjective,
    fit, patchFit,
  };
}
